import { createColumnHelper } from '@tanstack/react-table';
import { formatInTimeZone } from 'date-fns-tz';
import {
  COLUMN_DEFAULT_SIZE_BASE,
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { ReportMetricsResponse } from 'src/contexts/ReportMetricsContext/ReportMetricsContextV2';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { defaultColumnHelperAccessor } from 'src/tables/ReportTableCommon/DefaultColumnHelperAccessor';
import { LinkCell } from 'src/tables/ReportTableCommon/LinkCell';
import { SHIMMERING_DIV_HEIGHT_REPORT } from 'src/tables/ReportTableCommon/ReportTableCommon.constants';
import { reportV2AccessorFn } from 'src/tables/ReportTableCommon/ReportTableCommon.utils';
import { TableShimmeringDiv } from 'src/tables/Table/Table.styled';
import { ReportColumnPersonalizationFlags } from 'src/utils/columns/columnUtils.types';
import {
  PurchaseOrderLineItemReportTableGroupingColumnId,
  PurchaseOrderLineItemReportTableValueColumnId,
} from 'src/utils/columns/purchaseOrderLineItem/purchaseOrderLineItemColumnUtils.types';
import {
  PURCHASE_LINE_ITEM_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID,
  PURCHASE_LINE_ITEM_REPORT_TABLE_VALUE_COLUMN_ID_TO_CONTENT_ID,
} from 'src/utils/constants/contentIdMaps';
import { getPurchaseOrderRelativeUrl } from 'src/utils/purchaseUtils';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import { ReportValueType } from 'src/WebApiController';

const columnHelper = createColumnHelper<ReportMetricsResponse | null>();

export const PURCHASE_ORDER_LINE_ITEM_REPORT_SPECIAL_TABLE_COLUMNS_CONFIG = {
  [PurchaseOrderLineItemReportTableGroupingColumnId.PurchaseOrderId]:
    columnHelper.accessor(
      reportV2AccessorFn(
        PurchaseOrderLineItemReportTableGroupingColumnId.PurchaseOrderId
      ),
      {
        id: PurchaseOrderLineItemReportTableGroupingColumnId.PurchaseOrderId,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                PURCHASE_LINE_ITEM_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  PurchaseOrderLineItemReportTableGroupingColumnId
                    .PurchaseOrderId
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: ({ getValue }) => {
          const purchaseOrderId = getValue<string | null>();

          if (!purchaseOrderId) {
            return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
          }

          return (
            <LinkCell
              text={`#${purchaseOrderId}`}
              url={getPurchaseOrderRelativeUrl(purchaseOrderId)}
              separator={<span>{', '}</span>}
              openInNewTab
              align="left"
              style={{ fontWeight: 600 }}
            />
          );
        },
      }
    ),
  [PurchaseOrderLineItemReportTableGroupingColumnId.CostMonth]:
    columnHelper.accessor(
      reportV2AccessorFn(
        PurchaseOrderLineItemReportTableGroupingColumnId.CostMonth
      ),
      {
        id: PurchaseOrderLineItemReportTableGroupingColumnId.CostMonth,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                PURCHASE_LINE_ITEM_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  PurchaseOrderLineItemReportTableGroupingColumnId.CostMonth
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_BASE,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ getValue, row: { original } }) {
          if (!original) {
            return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
          }

          const dateValue = getValue<string | null | undefined>();

          return (
            <TableCellDiv align="left">
              <div>
                {dateValue
                  ? formatInTimeZone(new Date(dateValue), 'UTC', 'MMMM yyyy')
                  : ''}
              </div>
            </TableCellDiv>
          );
        },
      }
    ),
  [PurchaseOrderLineItemReportTableGroupingColumnId.CostWeek]:
    columnHelper.accessor(
      reportV2AccessorFn(
        PurchaseOrderLineItemReportTableGroupingColumnId.CostWeek
      ),
      {
        id: PurchaseOrderLineItemReportTableGroupingColumnId.CostWeek,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                PURCHASE_LINE_ITEM_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  PurchaseOrderLineItemReportTableGroupingColumnId.CostWeek
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_BASE,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ getValue, row: { original } }) {
          if (!original) {
            return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
          }

          const dateValue = getValue<string | null | undefined>();

          const dateEndOfWeek = dateValue ? new Date(dateValue) : null;
          if (dateEndOfWeek) {
            dateEndOfWeek.setDate(dateEndOfWeek.getDate() + 6);
          }

          return (
            <TableCellDiv align="left">
              <div>
                {dateValue && dateEndOfWeek
                  ? formatInTimeZone(new Date(dateValue), 'UTC', 'yyyy/MM/dd') +
                    ' - ' +
                    formatInTimeZone(dateEndOfWeek, 'UTC', 'yyyy/MM/dd')
                  : ''}
              </div>
            </TableCellDiv>
          );
        },
      }
    ),
  [PurchaseOrderLineItemReportTableGroupingColumnId.CostDay]:
    columnHelper.accessor(
      reportV2AccessorFn(
        PurchaseOrderLineItemReportTableGroupingColumnId.CostDay
      ),
      {
        id: PurchaseOrderLineItemReportTableGroupingColumnId.CostDay,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                PURCHASE_LINE_ITEM_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  PurchaseOrderLineItemReportTableGroupingColumnId.CostDay
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_BASE,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ getValue, row: { original } }) {
          if (!original) {
            return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
          }

          const dateValue = getValue<string | null | undefined>();

          return (
            <TableCellDiv align="left">
              <div>
                {dateValue
                  ? formatInTimeZone(new Date(dateValue), 'UTC', 'yyyy/MM/dd')
                  : ''}
              </div>
            </TableCellDiv>
          );
        },
      }
    ),
} as const;

export const purchaseOrderLineItemReportTableColumnDef = (
  columnId: string,
  columnValue: string,
  valueType: ReportValueType | undefined,
  personalizationFlags: Partial<ReportColumnPersonalizationFlags> | undefined
) => {
  const specialConfig = Object.entries(
    PURCHASE_ORDER_LINE_ITEM_REPORT_SPECIAL_TABLE_COLUMNS_CONFIG
  ).find(([key]) => key === columnId)?.[1];

  if (specialConfig) {
    return specialConfig;
  }

  const headerContentId =
    PURCHASE_LINE_ITEM_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
      columnId as unknown as PurchaseOrderLineItemReportTableGroupingColumnId
    ] ||
    PURCHASE_LINE_ITEM_REPORT_TABLE_VALUE_COLUMN_ID_TO_CONTENT_ID[
      columnId as unknown as PurchaseOrderLineItemReportTableValueColumnId
    ];

  return defaultColumnHelperAccessor(
    columnHelper,
    columnId,
    columnValue,
    headerContentId,
    ReportTypesV2.PurchaseOrderLineItem,
    valueType,
    personalizationFlags
  );
};
